import { useCallback, useSyncExternalStore } from "react";
import { useLocale } from "next-intl";
import {
  openPipelineStream,
  readSSE,
  type PipelineReportType,
} from "@/api/reports/pipelineStream";

export type PipelinePhase = "idle" | "running" | "finishing" | "done" | "warning" | "error";
export type StepState = "pending" | "in_progress" | "done";

/** Фиксированный порядок шагов пайплайна. */
export const PIPELINE_STEP_ORDER: PipelineReportType[] = [
  "transcribe",
  "actions",
  "q_density",
  "answers",
  "environment",
  "evaluation",
];

const initialSteps = (): Record<string, StepState> =>
  Object.fromEntries(PIPELINE_STEP_ORDER.map((r) => [r, "pending"]));

const allDoneSteps = (): Record<string, StepState> =>
  Object.fromEntries(PIPELINE_STEP_ORDER.map((r) => [r, "done"]));

/**
 * Под-шаги AI-генерации (этап evaluation) приходят без percent — бэк держит
 * прогресс на 75 всё время работы LLM. Маппим их на проценты внутри окна 75→98.
 */
const SUBSTEP_PERCENT: Record<string, number> = {
  gathering_data: 80,
  analyzing_data: 85,
  sending_for_analysis: 90,
  processing_response: 95,
  saving_result: 98,
};

// ─────────────────────────────────────────────────────────────────────────────
// ФОНОВЫЙ STORE с ОЧЕРЕДЬЮ (module-level). SSE-генерация живёт ВНЕ React-дерева:
//  • не умирает при закрытии дровера / смене вкладки / навигации по сайту
//    (жива, пока загружен SPA; гибнет только при F5 / закрытии вкладки);
//  • прогоны идут ПО ОДНОМУ — пока один SSE не завершится, следующий ждёт в
//    очереди (queued). Это бережёт бэкенд/канал.
// Компоненты подписываются на снимок по attemptId через useSyncExternalStore.
// ─────────────────────────────────────────────────────────────────────────────

export interface PipelineSnapshot {
  phase: PipelinePhase;
  percent: number;
  steps: Record<string, StepState>;
  subStep: string | null;
  warning: string | null;
  error: string | null;
  reports: Partial<Record<PipelineReportType, unknown>>;
  /** В очереди и ждёт своей очереди (активный прогон ещё не начался). */
  queued: boolean;
}

interface PipelineRuntime {
  controller: AbortController | null;
  collected: Partial<Record<PipelineReportType, unknown>>;
}

/** Кэш готовых результатов по attemptId (на время сессии). */
const pipelineReportsCache = new Map<number, Partial<Record<PipelineReportType, unknown>>>();
/** Снимки состояния по attemptId (источник для useSyncExternalStore). */
const entries = new Map<number, PipelineSnapshot>();
/** Несинхронизируемые рантайм-данные прогона (controller, аккумулятор). */
const runtime = new Map<number, PipelineRuntime>();
/** Подписчики (глобально; каждый хук перечитывает свой снимок). */
const listeners = new Set<() => void>();

// Очередь: один активный прогон + список ожидающих.
let activeId: number | null = null;
const waiting: number[] = [];
const pendingOpts = new Map<number, { locale: string; force: boolean }>();

const EMPTY: PipelineSnapshot = Object.freeze({
  phase: "idle",
  percent: 0,
  steps: Object.freeze(initialSteps()) as Record<string, StepState>,
  subStep: null,
  warning: null,
  error: null,
  reports: Object.freeze({}),
  queued: false,
});

function emit() {
  for (const l of listeners) l();
}

function buildInitial(id: number): PipelineSnapshot {
  const cached = pipelineReportsCache.get(id);
  if (cached) {
    return {
      phase: "done",
      percent: 100,
      steps: allDoneSteps(),
      subStep: null,
      warning: null,
      error: null,
      reports: cached,
      queued: false,
    };
  }
  return {
    phase: "idle",
    percent: 0,
    steps: initialSteps(),
    subStep: null,
    warning: null,
    error: null,
    reports: {},
    queued: false,
  };
}

/** Ленивая инициализация снимка (без мутаций «наружу», стабильная ссылка). */
function snapshot(id: number): PipelineSnapshot {
  let e = entries.get(id);
  if (!e) {
    e = buildInitial(id);
    entries.set(id, e);
  }
  return e;
}

// ВРЕМЕННО (диагностика "статус скачет" на мобилке): шлём переходы фаз в console
// (видно в консоли при ?debug=1). Сам gate — наличие debug=1.
let __pipeDbg: boolean | null = null;
function pipeDbgOn(): boolean {
  if (__pipeDbg !== null) return __pipeDbg;
  try {
    __pipeDbg =
      typeof window !== "undefined" &&
      (window.location.search.indexOf("debug=1") !== -1 ||
        window.localStorage.getItem("debug") === "1");
  } catch {
    __pipeDbg = false;
  }
  return !!__pipeDbg;
}

function patch(id: number, p: Partial<PipelineSnapshot>) {
  const prev = snapshot(id);
  entries.set(id, { ...prev, ...p });
  if (pipeDbgOn() && (p.phase !== undefined || p.queued !== undefined)) {
    const next = entries.get(id)!;
    // eslint-disable-next-line no-console
    console.info(
      `[pipeline-store id=${id}] phase ${prev.phase}->${next.phase} ` +
        `queued ${prev.queued}->${next.queued} percent=${next.percent} ` +
        `activeId=${activeId} waiting=[${waiting.join(",")}]`,
    );
  }
  emit();
}

const resetFields = (): Partial<PipelineSnapshot> => ({
  percent: 0,
  steps: initialSteps(),
  subStep: null,
  warning: null,
  error: null,
  reports: {},
});

function processQueue() {
  if (activeId !== null) return; // занято — ждём
  const nextId = waiting.shift();
  if (nextId === undefined) return;
  const opts = pendingOpts.get(nextId);
  pendingOpts.delete(nextId);
  if (!opts) {
    processQueue();
    return;
  }
  void runPipeline(nextId, opts.locale, opts.force);
}

async function runPipeline(id: number, locale: string, forceRegenerate: boolean) {
  activeId = id;
  const controller = new AbortController();
  runtime.set(id, { controller, collected: {} });
  patch(id, { phase: "running", queued: false, ...resetFields() });

  try {
    const res = await openPipelineStream(id, forceRegenerate, controller.signal);
    await readSSE(
      res,
      {
        status: (d) => {
          const types = d.report_types?.length ? d.report_types : PIPELINE_STEP_ORDER;
          patch(id, { steps: Object.fromEntries(types.map((r) => [r, "pending"])) });
        },
        progress: (d) => {
          const s = snapshot(id);
          const next: Partial<PipelineSnapshot> = {};
          if (typeof d.percent === "number") next.percent = Math.max(s.percent, d.percent);
          const stage = typeof d.stage === "string" ? d.stage : "";
          if (stage.endsWith("_started")) {
            const rt = stage.replace("_started", "");
            next.steps = { ...s.steps, [rt]: "in_progress" };
          }
          if (typeof d.step === "string") {
            next.subStep = d.step;
            const mapped = SUBSTEP_PERCENT[d.step];
            if (typeof mapped === "number") {
              next.percent = Math.max(next.percent ?? s.percent, mapped);
            }
          }
          if (Object.keys(next).length) patch(id, next);
        },
        result: (d) => {
          const s = snapshot(id);
          const rt = runtime.get(id);
          if (rt) rt.collected = { ...rt.collected, [d.report_type]: d.data };
          patch(id, {
            steps: { ...s.steps, [d.report_type]: "done" },
            reports: { ...s.reports, [d.report_type]: d.data },
          });
        },
        done: () => {
          const rt = runtime.get(id);
          if (rt) pipelineReportsCache.set(id, rt.collected);
          patch(id, { percent: 100, phase: "finishing" });
          setTimeout(() => completePipeline(id), 1500);
        },
        warning: (d) => {
          const text =
            locale === "en"
              ? d.summary_en
              : locale === "kz" || locale === "kk"
                ? d.summary_kz
                : d.summary_ru;
          // warning — НЕ терминальное событие: стрим продолжается и приходит done.
          // Только сохраняем текст, фазу НЕ трогаем (иначе лоадер пропадал и
          // показывалась кнопка/старый балл по середине генерации).
          patch(id, { warning: text ?? d.summary_en ?? d.summary_ru ?? null });
          if (pipeDbgOn()) {
            // eslint-disable-next-line no-console
            console.info(
              `[pipeline-warning id=${id}] ru="${d.summary_ru ?? ""}" en="${d.summary_en ?? ""}" kz="${d.summary_kz ?? ""}"`,
            );
          }
        },
        error: (d) => {
          patch(id, { error: d.detail ?? "error", phase: "error" });
        },
      },
      controller.signal,
    );
  } catch (e) {
    if (!controller.signal.aborted) {
      patch(id, { error: (e as Error).message, phase: "error" });
    }
  } finally {
    if (activeId === id) activeId = null;
    // Подстраховка: стрим закрылся, а терминального done/error не было (например,
    // последним пришёл warning) — не оставляем лоадер висеть, фиксируем собранное.
    if (!controller.signal.aborted) {
      const cur = snapshot(id);
      if (cur.phase === "running") {
        const rt = runtime.get(id);
        if (rt) pipelineReportsCache.set(id, rt.collected);
        patch(id, { percent: 100, phase: "finishing" });
        setTimeout(() => completePipeline(id), 1500);
      }
    }
    processQueue(); // следующий из очереди
  }
}

function startPipeline(id: number, locale: string, forceRegenerate: boolean) {
  const cur = snapshot(id);

  // Уже стримится этот id.
  if (activeId === id) {
    if (forceRegenerate) {
      // Перезапуск: ставим в начало очереди и обрываем текущий — finally подхватит.
      pendingOpts.set(id, { locale, force: true });
      waiting.unshift(id);
      runtime.get(id)?.controller?.abort();
    }
    return;
  }

  // Уже ждёт в очереди.
  if (waiting.includes(id)) {
    if (forceRegenerate) pendingOpts.set(id, { locale, force: true });
    return;
  }

  // Идёт / завершается / уже готово — не дёргаем (если не force).
  if (!forceRegenerate && (cur.phase === "running" || cur.phase === "finishing" || cur.phase === "done")) {
    return;
  }

  // Ставим в очередь и сразу показываем состояние «в очереди» (или старт, если очередь пуста).
  pendingOpts.set(id, { locale, force: forceRegenerate });
  waiting.push(id);
  patch(id, { phase: "running", queued: activeId !== null, ...resetFields() });
  processQueue();
}

function completePipeline(id: number) {
  const cur = snapshot(id);
  if (cur.phase === "finishing") patch(id, { phase: "done" });
}

function cancelPipeline(id: number) {
  // Убрать из очереди.
  const w = waiting.indexOf(id);
  if (w !== -1) waiting.splice(w, 1);
  pendingOpts.delete(id);
  // Оборвать активный.
  runtime.get(id)?.controller?.abort();
  runtime.set(id, { controller: null, collected: {} });
  if (activeId === id) {
    activeId = null;
    processQueue();
  }
  const cached = pipelineReportsCache.get(id);
  patch(id, cached ? { phase: "done", percent: 100, queued: false } : { phase: "idle", queued: false, ...resetFields() });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// Предупреждение при закрытии/перезагрузке вкладки во время генерации
// (активный прогон или непустая очередь). Регистрируется один раз.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (activeId !== null || waiting.length > 0) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}

/**
 * Подписка на фоновую SSE-генерацию AI-отчётов по attemptId.
 *
 * Генерация живёт в module-store: НЕ обрывается при размонтировании (дровер/
 * вкладка/навигация), идёт пока загружен SPA. Прогоны выполняются ПО ОЧЕРЕДИ —
 * один за другим. Останавливается на done/error или явном cancel/refresh.
 */
export function usePipelineSSE(attemptId: number | null) {
  const locale = useLocale();

  const snap = useSyncExternalStore(
    subscribe,
    () => (attemptId != null ? snapshot(attemptId) : EMPTY),
    () => EMPTY,
  );

  const start = useCallback(
    (forceRegenerate = false) => {
      if (attemptId != null) startPipeline(attemptId, locale, forceRegenerate);
    },
    [attemptId, locale],
  );

  const cancel = useCallback(() => {
    if (attemptId != null) cancelPipeline(attemptId);
  }, [attemptId]);

  const complete = useCallback(() => {
    if (attemptId != null) completePipeline(attemptId);
  }, [attemptId]);

  return {
    phase: snap.phase,
    percent: snap.percent,
    steps: snap.steps,
    subStep: snap.subStep,
    warning: snap.warning,
    error: snap.error,
    reports: snap.reports,
    queued: snap.queued,
    start,
    cancel,
    complete,
  };
}

export type PipelineState = ReturnType<typeof usePipelineSSE>;
